import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, compressImage, formatApiErrorDetail } from "@/lib/api";
import { RESP_STATUSES, RESP_STATUS_LABELS } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Plus, Trash2, User } from "lucide-react";

const NONE = "__none__";
const ALL = "__all__";

const EMPTY = {
  name: "", cpf: "", position: "", function: "", department: "", registration: "",
  email: "", phone: "", whatsapp: "", extension: "", photo: "",
  superior_id: null, status: "ativo", assignments: [],
};

function AssignmentRow({ a, tree, companies, onChange, onRemove, index }) {
  const client = tree.find((c) => c.id === a.client_id);
  const unit = client?.units?.find((u) => u.id === a.unit_id);
  const area = unit?.areas?.find((ar) => ar.id === a.area_id);
  const sel = (value, items, placeholder, onVal, testid) => (
    <Select value={value || ALL} onValueChange={(v) => onVal(v === ALL ? null : v)}>
      <SelectTrigger className="h-8 text-xs" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-center rounded-md border border-border p-2" data-testid={`assignment-row-${index}`}>
      {companies.length > 0 ? sel(a.company_id, companies, "Todas empresas", (v) => onChange({ ...a, company_id: v }), `assignment-company-${index}`) : <div className="hidden sm:block" />}
      {sel(a.client_id, tree, "Todos clientes", (v) => onChange({ company_id: a.company_id, client_id: v, unit_id: null, area_id: null, subarea_id: null }), `assignment-client-${index}`)}
      {sel(a.unit_id, client?.units || [], "Todas unidades", (v) => onChange({ ...a, unit_id: v, area_id: null, subarea_id: null }), `assignment-unit-${index}`)}
      {sel(a.area_id, unit?.areas || [], "Todas áreas", (v) => onChange({ ...a, area_id: v, subarea_id: null }), `assignment-area-${index}`)}
      <div className="flex gap-1 items-center">
        <div className="flex-1">{sel(a.subarea_id, area?.subareas || [], "Todas subáreas", (v) => onChange({ ...a, subarea_id: v }), `assignment-subarea-${index}`)}</div>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive" onClick={onRemove} data-testid={`assignment-remove-${index}`}>
          <Trash2 size={13} />
        </Button>
      </div>
    </div>
  );
}

export default function ResponsibleDialog({ open, onOpenChange, initial, responsibles, tree, companies, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const photoRef = useRef(null);

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
  }, [open, initial]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nome é obrigatório");
    setSaving(true);
    try {
      const body = { ...form, superior_id: form.superior_id || null };
      delete body.id; delete body.substitute; delete body.history; delete body.created_at;
      if (initial?.id) {
        await api.put(`/responsibles/${initial.id}`, body);
        toast.success("Responsável atualizado");
      } else {
        await api.post("/responsibles", body);
        toast.success("Responsável cadastrado");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, placeholder = "", type = "text") => (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={form[key]} onChange={(e) => set({ [key]: e.target.value })} placeholder={placeholder}
        className="h-9" data-testid={`resp-${key}-input`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="responsible-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{initial?.id ? "Editar Responsável" : "Novo Responsável"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="flex items-center gap-4">
            <button onClick={() => photoRef.current?.click()} data-testid="resp-photo-btn"
              className="h-16 w-16 rounded-full border border-border bg-muted flex items-center justify-center overflow-hidden shrink-0 hover:border-primary/60 transition-colors">
              {form.photo ? <img src={form.photo} alt="Foto" className="w-full h-full object-cover" /> : <User size={22} className="text-muted-foreground" />}
            </button>
            <input ref={photoRef} type="file" accept="image/*" hidden
              onChange={async (e) => { const f = e.target.files?.[0]; if (f) set({ photo: await compressImage(f, 320, 0.7) }); }} />
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">{field("Nome completo *", "name", "Ex.: João da Silva")}</div>
              <div className="space-y-1.5">
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => set({ status: v })}>
                  <SelectTrigger className="h-9" data-testid="resp-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RESP_STATUSES.map((s) => <SelectItem key={s} value={s}>{RESP_STATUS_LABELS[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground shrink-0" onClick={() => photoRef.current?.click()}>
              <Camera size={13} className="mr-1" /> Foto
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {field("Cargo", "position", "Ex.: Supervisor de Produção")}
            {field("Função", "function", "Ex.: Responsável de área")}
            {field("Departamento", "department", "Ex.: Qualidade")}
            {field("Matrícula", "registration")}
            {field("CPF (opcional)", "cpf", "000.000.000-00")}
            <div className="space-y-1.5">
              <Label className="text-xs">Superior imediato</Label>
              <Select value={form.superior_id || NONE} onValueChange={(v) => set({ superior_id: v === NONE ? null : v })}>
                <SelectTrigger className="h-9" data-testid="resp-superior-select"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Nenhum</SelectItem>
                  {responsibles.filter((r) => r.id !== initial?.id).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {field("E-mail", "email", "email@empresa.com", "email")}
            {field("Telefone", "phone", "(00) 0000-0000")}
            {field("WhatsApp", "whatsapp", "(00) 90000-0000")}
            {field("Ramal", "extension")}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Vínculos de responsabilidade (empresa → cliente → unidade → área → subárea)</Label>
              <Button variant="outline" size="sm" className="h-7 text-xs" data-testid="assignment-add-btn"
                onClick={() => set({ assignments: [...form.assignments, { company_id: null, client_id: null, unit_id: null, area_id: null, subarea_id: null }] })}>
                <Plus size={13} className="mr-1" /> Adicionar vínculo
              </Button>
            </div>
            {form.assignments.length === 0 && (
              <p className="text-xs text-muted-foreground border border-dashed border-border rounded-md py-4 text-center">
                Nenhum vínculo. Adicione ao menos um para que o responsável seja atribuído automaticamente às ocorrências do local.
              </p>
            )}
            <div className="space-y-2">
              {form.assignments.map((a, i) => (
                <AssignmentRow key={i} a={a} index={i} tree={tree} companies={companies}
                  onChange={(na) => set({ assignments: form.assignments.map((x, j) => (j === i ? na : x)) })}
                  onRemove={() => set({ assignments: form.assignments.filter((_, j) => j !== i) })} />
              ))}
            </div>
          </div>

          <Button className="w-full" onClick={save} disabled={saving} data-testid="resp-save-btn">
            {saving ? "Salvando..." : initial?.id ? "Salvar Alterações" : "Cadastrar Responsável"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
